import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Box, Card, CardActionArea, Grid, Typography } from "@mui/material";

import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import QueryStatsIcon from "@mui/icons-material/QueryStats";

import { PERMISSIONS } from "../../constants";

// The Usuarios module, like the PQRSD hub: one tile per part of it the user
// may use. With a single one there is nothing to choose, so it opens directly.
export const usersHubOptions = (user) => {
  const perms = user?.permissions || [];
  const isAdmin = (user?.roles || []).includes("ADMIN");
  const can = (p) => isAdmin || perms.includes(p);
  const iconSx = { fontSize: { xs: 44, md: 56 } };
  const items = [];
  if (can(PERMISSIONS.USER_ADMIN.ADD) || can(PERMISSIONS.USER_ADMIN.MANAGE) || can(PERMISSIONS.USER_ADMIN.DEACTIVATE)) {
    items.push({
      key: "admin",
      label: "Administrar usuarios",
      description: "Crea usuarios, edita perfiles, roles y correo, y habilita o deshabilita cuentas.",
      icon: <ManageAccountsIcon sx={iconSx} />,
      path: "/users/admin",
    });
  }
  if (can(PERMISSIONS.USER_ADMIN.MANAGE)) {
    items.push({
      key: "reports",
      label: "Informes",
      description: "Usuarios activos e inactivos, cambios de perfil y listados en Excel o CSV.",
      icon: <QueryStatsIcon sx={iconSx} />,
      path: "/users/reports",
    });
  }
  return items;
};

export default function UsersHub() {
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.value);
  const options = useMemo(() => usersHubOptions(user), [user]);

  useEffect(() => {
    if (options.length === 1) navigate(options[0].path, { replace: true });
  }, [options, navigate]);

  if (options.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6" gutterBottom>
          Sin acceso a Usuarios
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Tu usuario no tiene permisos para ningún módulo de Usuarios.
        </Typography>
      </Box>
    );
  }

  // While the single-option redirect runs, render nothing to avoid a flash.
  if (options.length === 1) return null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "70vh",
        px: { xs: 2, md: 4 },
        py: 4,
      }}
    >
      <Typography variant="h4" gutterBottom>
        Usuarios
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Selecciona un módulo
      </Typography>

      <Grid container spacing={3} justifyContent="center" sx={{ width: "100%", maxWidth: 900 }}>
        {options.map((opt) => (
          <Grid item xs={12} sm={6} md={4} key={opt.key}>
            <Card
              sx={{
                height: "100%",
                transition: "box-shadow 0.2s ease, transform 0.2s ease",
                "&:hover": { boxShadow: 6, transform: "translateY(-4px)" },
              }}
            >
              <CardActionArea onClick={() => navigate(opt.path)} sx={{ height: "100%" }}>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", p: 3 }}>
                  <Box sx={{ color: "primary.main", mb: 1.5 }}>{opt.icon}</Box>
                  <Typography variant="h6" gutterBottom>
                    {opt.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {opt.description}
                  </Typography>
                </Box>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
